import type { EliminationFormatConfig } from "./api";

export interface EliminationDirectAdvancerSummary {
  technicalByeCount: number;
  directAdvancerCount: number;
  preliminaryMatchCount: number;
  drawSize: number | null;
  nextRoundSize: number | null;
  hasDirectAdvancers: boolean;
  primaryLabel: string;
  helperText: string;
}

function positiveIntegerOrNull(value: unknown): number | null {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) return null;
  return Math.floor(numberValue);
}

export function getEliminationDirectAdvancerSummary(params: {
  participantCount: number;
  technicalByeCount?: number | null;
  config?: Pick<EliminationFormatConfig, "bracket_size" | "first_round_bye_count"> | null;
}): EliminationDirectAdvancerSummary {
  const participantCount = Math.max(0, Math.floor(params.participantCount || 0));
  const technicalByeCount = Math.max(0, Math.floor(params.technicalByeCount || 0));
  const configuredDrawSize = positiveIntegerOrNull(params.config?.bracket_size);

  if (!configuredDrawSize || participantCount <= 0) {
    return {
      technicalByeCount,
      directAdvancerCount: 0,
      preliminaryMatchCount: 0,
      drawSize: configuredDrawSize,
      nextRoundSize: configuredDrawSize,
      hasDirectAdvancers: false,
      primaryLabel: `${technicalByeCount} BYE${technicalByeCount === 1 ? "" : "s"}`,
      helperText: "Descansos automáticos",
    };
  }

  if (participantCount > configuredDrawSize) {
    const preliminaryMatchCount = Math.max(participantCount - configuredDrawSize, 0);
    const directAdvancerCount = Math.max(configuredDrawSize - preliminaryMatchCount, 0);
    return {
      technicalByeCount,
      directAdvancerCount,
      preliminaryMatchCount,
      drawSize: configuredDrawSize,
      nextRoundSize: configuredDrawSize,
      hasDirectAdvancers: directAdvancerCount > 0,
      primaryLabel: `${directAdvancerCount} pasan directo`,
      helperText: `${preliminaryMatchCount} preliminar${preliminaryMatchCount === 1 ? "" : "es"} · a Ronda ${configuredDrawSize}`,
    };
  }

  const configuredByeCount = params.config?.first_round_bye_count;
  if (configuredByeCount !== null && configuredByeCount !== undefined) {
    const directAdvancerCount = Math.max(0, Math.min(Number(configuredByeCount) || 0, participantCount));
    const preliminaryMatchCount = Math.max(0, Math.floor((participantCount - directAdvancerCount) / 2));
    return {
      technicalByeCount,
      directAdvancerCount,
      preliminaryMatchCount,
      drawSize: configuredDrawSize,
      nextRoundSize: preliminaryMatchCount > 0 ? configuredDrawSize / 2 : configuredDrawSize,
      hasDirectAdvancers: directAdvancerCount > 0,
      primaryLabel: directAdvancerCount > 0
        ? `${directAdvancerCount} descanso${directAdvancerCount === 1 ? "" : "s"}`
        : `${technicalByeCount} BYE${technicalByeCount === 1 ? "" : "s"}`,
      helperText: directAdvancerCount > 0
        ? "Pasan directo por configuración"
        : "Sin descansos configurados",
    };
  }

  return {
    technicalByeCount,
    directAdvancerCount: 0,
    preliminaryMatchCount: 0,
    drawSize: configuredDrawSize,
    nextRoundSize: configuredDrawSize,
    hasDirectAdvancers: false,
    primaryLabel: `${technicalByeCount} BYE${technicalByeCount === 1 ? "" : "s"}`,
    helperText: technicalByeCount > 0 ? "Partidos BYE generados" : "Sin descansos automáticos",
  };
}
